import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getLicensingPortalUrl } from '../config/api-config';

interface UserMenuProps {
  className?: string;
  /**
   * Opens the unified sign-in dialog (LicenseManagementDialog). The dialog
   * collects whatever is missing — license key and/or credentials — so this
   * button is always enabled, replacing the old disabled-until-license state.
   */
  onSignInClick?: () => void;
}

const UserMenu: React.FC<UserMenuProps> = ({ className = '', onSignInClick }) => {
  const { state, logout } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Loading state
  if (state.isLoading) {
    return (
      <div className={`flex items-center ${className}`}>
        <div className="animate-pulse flex items-center space-x-2">
          <div className="rounded-full bg-gray-300 h-8 w-8"></div>
          <div className="h-4 bg-gray-300 rounded w-20"></div>
        </div>
      </div>
    );
  }

  // Not authenticated - show login button (opens the unified sign-in dialog)
  if (!state.isAuthenticated && !state.isDevelopmentMode) {
    return (
      <div className={`flex items-center ${className}`}>
        <button
          onClick={onSignInClick}
          title="Sign in with your CAVU credentials and license key"
          className="flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
        >
          <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
          </svg>
          Sign In
        </button>
      </div>
    );
  }

  // Environment indicator — only renders in qa or development. In production
  // the user should NEVER see this badge (a "Dev Mode" label on prod would be
  // misleading and used to appear during cold-start auth failures).
  const envLabel = process.env.REACT_APP_ENV === 'qa'
    ? 'QA Mode'
    : process.env.REACT_APP_ENV === 'development'
      ? 'Dev Mode'
      : null;
  if (state.isDevelopmentMode && !state.isAuthenticated && envLabel) {
    return (
      <div className={`flex items-center ${className}`}>
        <div className="flex items-center px-3 py-1.5 bg-yellow-100 text-yellow-800 text-sm rounded-lg border border-yellow-200">
          <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          {envLabel}
        </div>
      </div>
    );
  }

  // Authenticated - show user menu
  const user = state.user;
  const initials = user?.displayName
    ? user.displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : user?.email?.[0]?.toUpperCase() || 'U';

  return (
    <div className={`relative ${className}`} ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
      >
        {/* Avatar */}
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white text-sm font-medium">
          {initials}
        </div>

        {/* Name and dropdown arrow */}
        <div className="hidden sm:flex items-center">
          <span className="text-sm font-medium text-gray-700 max-w-32 truncate">
            {user?.displayName || user?.email || 'User'}
          </span>
          <svg className={`w-4 h-4 ml-1 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}
               fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-50">
          {/* User info */}
          <div className="px-4 py-3 border-b border-gray-100">
            <p className="text-sm font-medium text-gray-900">{user?.displayName || 'User'}</p>
            <p className="text-xs text-gray-500 truncate">{user?.email}</p>
            {(user?.customerName || user?.customerId) && (
              <p className="text-xs text-gray-400 mt-1">
                Customer: {user.customerName || user.customerId}
              </p>
            )}
            {user?.customerRole && (
              <span className={`inline-block mt-1 px-2 py-0.5 text-xs rounded-full ${
                user.customerRole === 'ADMIN' || user.customerRole === 'OWNER'
                  ? 'bg-purple-100 text-purple-700'
                  : 'bg-gray-100 text-gray-600'
              }`}>
                {user.customerRole}
              </span>
            )}
          </div>

          {/* Menu items */}
          <div className="py-1">
            <a
              href={getLicensingPortalUrl('/customer/dashboard')}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setIsOpen(false)}
              className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              <svg className="w-4 h-4 mr-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <span className="flex-grow text-left">Manage Account</span>
              <svg className="w-3.5 h-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </div>

          {/* Logout */}
          <div className="border-t border-gray-100 pt-1">
            <button
              onClick={() => {
                setIsOpen(false);
                logout();
              }}
              className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50"
            >
              <svg className="w-4 h-4 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Sign Out
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserMenu;
